import React, { useState, useEffect } from 'react';
import ValentineCard from './ValentineCard';
import SuccessModal from './SuccessModal';

const App: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFinalPage, setIsFinalPage] = useState(false);
  const [hearts, setHearts] = useState<{ id: number, left: string, size: string, duration: string, delay: string }[]>([]);

  // Background floating hearts animation
  useEffect(() => {
    const createHeart = () => {
      const id = Date.now();
      const left = `${Math.random() * 100}%`;
      const size = `${Math.random() * 20 + 20}px`;
      const duration = `${Math.random() * 5 + 5}s`;

      setHearts(prev => [...prev, { id, left, size, duration, delay: '0s' }]);

      setTimeout(() => {
        setHearts(prev => prev.filter(h => h.id !== id));
      }, 10000);
    };

    const interval = setInterval(createHeart, isFinalPage ? 150 : 600);
    return () => clearInterval(interval);
  }, [isFinalPage]);

  if (isFinalPage) {
    return (
      <main className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#ff758f] via-[#ff8fa3] to-[#ffb3c1] animate-in fade-in duration-1000">
        {/* Intense Flying Hearts */}
        {hearts.map(heart => (
          <div
            key={heart.id}
            className="heart text-red-100 opacity-80 z-0 pointer-events-none drop-shadow-sm"
            style={{
              left: heart.left,
              fontSize: heart.size,
              animationDuration: heart.duration,
            }}
          >
            ❤️
          </div>
        ))}

        <div className="relative z-10 text-center px-6 transform animate-in zoom-in slide-in-from-bottom-20 duration-1000">
          <div className="text-[5rem] md:text-[7rem] leading-none mb-4 animate-pulse drop-shadow-2xl">💍</div>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 font-serif drop-shadow-lg leading-tight">
            I Love You <br />
            <span className="text-pink-100 italic">Forever</span>...
          </h1>

          <h2 className="inline-block text-2xl md:text-4xl font-bold bg-white text-red-500 mb-8 font-serif drop-shadow-md px-8 py-4 rounded-full animate-bounce shadow-xl transform hover:scale-105 transition-all">
            Happy Valentine's Day! 💖
          </h2>
          <p className="text-white/90 text-xl md:text-2xl font-medium max-w-2xl mx-auto italic drop-shadow-md leading-relaxed">
            "You are the best thing that ever happened to me. Every moment with you is a gift I'll cherish for the rest of my life."
          </p>

          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <div className="px-6 py-2 bg-white/20 backdrop-blur-md rounded-full text-white font-bold border border-white/40 text-sm md:text-base">
              Forever & Always ♾️
            </div>
            <div className="px-6 py-2 bg-white/20 backdrop-blur-md rounded-full text-white font-bold border border-white/40 text-sm md:text-base">
              My Only One 🌹
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#ff9a9e] via-[#fecfef] to-[#fad0c4]">
      {/* Floating Hearts Background */}
      {hearts.map(heart => (
        <div
          key={heart.id}
          className="heart text-red-300 opacity-40 z-0 pointer-events-none"
          style={{
            left: heart.left,
            fontSize: heart.size,
            animationDuration: heart.duration,
          }}
        >
          ❤️
        </div>
      ))}

      {/* Main Content */}
      <div className="relative z-10 w-full flex flex-col items-center">
        <ValentineCard onYesClick={() => setIsModalOpen(true)} />
      </div>

      <SuccessModal
        isOpen={isModalOpen}
        onConfirm={() => {
          setIsModalOpen(false);
          setIsFinalPage(true);
        }}
      />

      <div className="absolute bottom-6 text-pink-800/60 text-sm font-medium tracking-wide">
        Made with all my love for you 💝
      </div>
    </main>
  );
};

export default App;
